/* eslint-disable */
// Centralised theme tokens + global stylesheet for the Hosted Proposal module.
// V2 — premium international consulting aesthetic: soft light surfaces, navy
// + royal-blue accents, restrained gold premium highlights, elegant serif
// headings paired with clean sans-serif body. Inspired by Stripe / Deel /
// Linear / Remote.com / modern VC proposal decks.

export const proposalTheme = {
  // Base surfaces
  bgBase:       "#F6F8FC",   // off-white canvas
  bgSoft:       "#FFFFFF",   // panel
  bgTint:       "#EEF2FA",   // subtle hero tint
  // Text
  textPrimary:  "#0B1735",   // deep navy
  textBody:     "rgba(15, 28, 60, 0.78)",
  textMuted:    "rgba(15, 28, 60, 0.55)",
  // Accents
  navy:         "#0A2A8A",   // royal navy
  royal:        "#2D52E0",   // royal blue
  royalSoft:    "#5B7CF0",
  gold:         "#C9A961",   // premium gold highlight
  goldSoft:     "#E5D4A6",
  // Lines / borders
  borderSoft:   "rgba(15, 31, 75, 0.08)",
  borderStrong: "rgba(15, 31, 75, 0.14)",
  // Backwards-compatible aliases (kept so existing components that
  // referenced these tokens continue to work without code changes).
  bgPanel:      "#FFFFFF",
  surface:      "#FFFFFF",
  accent:       "#2D52E0",   // royal blue
  accentDeep:   "#0A2A8A",   // navy
  // Typography
  fontHead:     "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
  fontBody:     "'Inter', 'Manrope', system-ui, sans-serif",
};

// Global stylesheet injected once per render. Class names remain stable
// (`.miq-prop`, `.miq-glass`, `.eyebrow`, `.accent`, `.miq-card`, etc.)
// so individual section renderers don't need to change — the entire
// visual identity flips with this single file.
export const proposalStylesheet = `
  /* ── Root surface ────────────────────────────────────────────────── */
  .miq-prop {
    background:
      radial-gradient(900px 600px at 12% -10%, rgba(45, 82, 224, 0.08), transparent 70%),
      radial-gradient(700px 500px at 100% 0%, rgba(201, 169, 97, 0.07), transparent 70%),
      linear-gradient(180deg, ${proposalTheme.bgTint} 0%, ${proposalTheme.bgBase} 22%, ${proposalTheme.bgBase} 100%);
    color: ${proposalTheme.textPrimary};
    font-family: ${proposalTheme.fontBody};
    min-height: 100vh;
    font-feature-settings: "ss01", "cv02", "cv11";
  }

  /* ── Typography utilities ────────────────────────────────────────── */
  .miq-prop .serif {
    font-family: ${proposalTheme.fontHead};
    font-weight: 500;
    letter-spacing: -0.012em;
  }
  .miq-prop .eyebrow {
    color: ${proposalTheme.royal};
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.22em;
  }
  .miq-prop .accent     { color: ${proposalTheme.royal}; }
  .miq-prop .gold       { color: ${proposalTheme.gold}; }
  .miq-prop .navy       { color: ${proposalTheme.navy}; }

  /* ── Surfaces ────────────────────────────────────────────────────── */
  .miq-glass {
    background: ${proposalTheme.bgSoft};
    border: 1px solid ${proposalTheme.borderSoft};
    border-radius: 16px;
    box-shadow:
      0 1px 0 rgba(15, 31, 75, 0.02),
      0 24px 60px -28px rgba(15, 31, 75, 0.18);
  }
  .miq-card { padding: 30px; }
  .miq-hr {
    height: 2px;
    background: linear-gradient(90deg, ${proposalTheme.royal} 0%, ${proposalTheme.gold} 100%);
    border-radius: 999px;
    opacity: 0.65;
  }

  /* ── Layout ──────────────────────────────────────────────────────── */
  .miq-section          { padding: 64px 24px; }
  .miq-section--narrow  { max-width: 920px; margin: 0 auto; }
  .miq-section--wide    { max-width: 1180px; margin: 0 auto; }
  .miq-grid-2 { display: grid; grid-template-columns: 1fr; gap: 18px; }
  .miq-grid-4 { display: grid; grid-template-columns: 1fr; gap: 18px; }
  @media (min-width: 720px) {
    .miq-grid-2 { grid-template-columns: 1fr 1fr; }
    .miq-grid-4 { grid-template-columns: repeat(2, 1fr); }
    .miq-section { padding: 96px 40px; }
  }
  @media (min-width: 1100px) {
    .miq-grid-4 { grid-template-columns: repeat(4, 1fr); }
  }

  /* ── Display sizes ───────────────────────────────────────────────── */
  .miq-h1 {
    font-size: clamp(40px, 6.8vw, 76px);
    line-height: 1.04;
    letter-spacing: -0.022em;
    color: ${proposalTheme.textPrimary};
  }
  .miq-h2 {
    font-size: clamp(28px, 4vw, 44px);
    line-height: 1.08;
    letter-spacing: -0.014em;
    color: ${proposalTheme.textPrimary};
  }
  .miq-body   { color: ${proposalTheme.textBody};  font-size: 16.5px; line-height: 1.78; font-weight: 400; }
  .miq-small  { color: ${proposalTheme.textMuted}; font-size: 12.5px; letter-spacing: 0.04em; }

  /* ── Numbers (used in payment plan etc.) ─────────────────────────── */
  .miq-pct {
    background: linear-gradient(135deg, ${proposalTheme.navy} 0%, ${proposalTheme.royal} 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-family: ${proposalTheme.fontHead};
    font-size: 48px;
    line-height: 1;
    letter-spacing: -0.02em;
    font-weight: 600;
  }
  .miq-step-no {
    color: ${proposalTheme.royal};
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.32em;
    text-transform: uppercase;
  }

  /* ── Header / lockup ─────────────────────────────────────────────── */
  .miq-logo-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 26px 32px;
    border-bottom: 1px solid ${proposalTheme.borderSoft};
    background: rgba(255, 255, 255, 0.65);
    backdrop-filter: saturate(140%) blur(8px);
    -webkit-backdrop-filter: saturate(140%) blur(8px);
    position: sticky; top: 0; z-index: 10;
  }
  .miq-logo-row__brand {
    color: ${proposalTheme.textPrimary};
    font-weight: 700;
    letter-spacing: 0.04em;
    font-size: 17px;
  }
  .miq-logo-row__brand span { color: ${proposalTheme.royal}; }
  .miq-logo-row__partner {
    display: flex; align-items: center; gap: 12px;
    color: ${proposalTheme.textMuted}; font-size: 12.5px;
  }
  .miq-logo-row__partner img { max-height: 36px; max-width: 160px; object-fit: contain; }

  /* ── Trust strip (premium meta badges below header) ──────────────── */
  .miq-trust-strip {
    display: flex; flex-wrap: wrap; gap: 10px;
    padding: 14px 32px 0;
    background: transparent;
    max-width: 1180px; margin: 0 auto;
  }
  .miq-trust-chip {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 6px 12px;
    background: ${proposalTheme.bgSoft};
    border: 1px solid ${proposalTheme.borderSoft};
    color: ${proposalTheme.textBody};
    border-radius: 999px;
    font-size: 11.5px; font-weight: 600;
    letter-spacing: 0.04em;
  }
  .miq-trust-chip .dot {
    width: 6px; height: 6px; border-radius: 999px;
    background: ${proposalTheme.royal};
    box-shadow: 0 0 0 3px rgba(45, 82, 224, 0.16);
  }
  .miq-trust-chip.gold .dot {
    background: ${proposalTheme.gold};
    box-shadow: 0 0 0 3px rgba(201, 169, 97, 0.18);
  }

  /* ── CTAs ────────────────────────────────────────────────────────── */
  .miq-cta-row { display: flex; flex-wrap: wrap; gap: 12px; }
  .miq-btn-primary {
    background: linear-gradient(135deg, ${proposalTheme.royal} 0%, ${proposalTheme.navy} 100%);
    color: #FFFFFF;
    border-radius: 999px;
    padding: 15px 26px;
    font-weight: 600;
    font-size: 14.5px;
    letter-spacing: 0.02em;
    cursor: pointer;
    border: 0;
    box-shadow: 0 12px 30px -10px rgba(45, 82, 224, 0.45);
    transition: transform .22s ease, box-shadow .22s ease, filter .22s ease;
  }
  .miq-btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 42px -10px rgba(45, 82, 224, 0.55);
    filter: brightness(1.05);
  }
  .miq-btn-ghost {
    background: ${proposalTheme.bgSoft};
    color: ${proposalTheme.textPrimary};
    border: 1px solid ${proposalTheme.borderStrong};
    border-radius: 999px;
    padding: 15px 26px;
    font-weight: 600;
    font-size: 14.5px;
    cursor: pointer;
    transition: all .22s ease;
  }
  .miq-btn-ghost:hover {
    background: ${proposalTheme.bgTint};
    border-color: ${proposalTheme.royal};
    color: ${proposalTheme.royal};
  }

  /* ── Pricing table (used by ProposalPreview PricingBlock) ────────── */
  .miq-pricing-wrap {
    border: 1px solid ${proposalTheme.borderSoft};
    border-radius: 18px;
    overflow: hidden;
    background: ${proposalTheme.bgSoft};
    box-shadow: 0 24px 60px -28px rgba(15, 31, 75, 0.18);
  }
  .miq-pricing-scroll { overflow-x: auto; }
  .miq-pricing-table {
    width: 100%;
    min-width: 640px;
    border-collapse: collapse;
    color: ${proposalTheme.textPrimary};
    table-layout: fixed;
  }
  .miq-pricing-table thead tr {
    background: linear-gradient(180deg, #F4F7FD 0%, #EEF2FA 100%);
    border-bottom: 1px solid ${proposalTheme.borderSoft};
  }
  .miq-pricing-table th {
    text-align: left; padding: 18px 20px;
    font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
    color: ${proposalTheme.navy}; font-weight: 700;
  }
  .miq-pricing-table th.num { text-align: right; padding-left: 14px; padding-right: 14px; }
  .miq-pricing-table tbody tr {
    border-bottom: 1px solid ${proposalTheme.borderSoft};
    transition: background-color .15s ease;
  }
  .miq-pricing-table tbody tr:hover { background: #FAFBFE; }
  .miq-pricing-table td {
    padding: 22px 20px; vertical-align: top; font-size: 14.5px;
  }
  .miq-pricing-table td.num {
    text-align: right; padding-left: 14px; padding-right: 14px;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    white-space: nowrap;
  }
  .miq-pricing-table td.line-total { font-weight: 700; font-size: 15px; }
  .miq-pricing-table tfoot tr.subtotal td { padding: 14px 20px; color: ${proposalTheme.textBody}; font-size: 13px; }
  .miq-pricing-table tfoot tr.discount td { padding: 6px 20px; color: #1B7A4E; font-size: 13px; }
  .miq-pricing-table tfoot tr.grand-total {
    border-top: 2px solid ${proposalTheme.navy};
    background: linear-gradient(180deg, #FAFBFE 0%, #F4F7FD 100%);
  }
  .miq-pricing-table tfoot tr.grand-total td {
    padding: 22px 20px;
    text-transform: uppercase;
    font-size: 13px;
    letter-spacing: 0.12em;
    font-weight: 700;
    color: ${proposalTheme.navy};
  }
  .miq-pricing-table tfoot tr.grand-total td.num {
    font-size: 26px;
    letter-spacing: -0.01em;
    text-transform: none;
    font-weight: 800;
  }
  .miq-svc-name { font-weight: 700; font-size: 15.5px; color: ${proposalTheme.textPrimary}; }
  .miq-svc-desc { font-size: 12.5px; color: ${proposalTheme.textMuted}; margin-top: 4px; line-height: 1.55; }

  /* ── Footer ──────────────────────────────────────────────────────── */
  .miq-footer {
    padding: 40px 24px;
    border-top: 1px solid ${proposalTheme.borderSoft};
    color: ${proposalTheme.textMuted};
    font-size: 12.5px;
    text-align: center;
    background: rgba(255, 255, 255, 0.6);
  }

  /* ── Print / PDF ─────────────────────────────────────────────────── */
  @media print {
    .miq-prop, .miq-prop * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .miq-prop { background: #FFFFFF !important; }
    .miq-section { page-break-inside: avoid; padding: 36px 24px; }
    .miq-logo-row { position: static !important; backdrop-filter: none !important; -webkit-backdrop-filter: none !important; background: #FFFFFF !important; }
    .miq-no-print { display: none !important; }
  }

  /* ── Subtle entrance animation on first paint ────────────────────── */
  @keyframes miq-rise {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .miq-section { animation: miq-rise .6s ease both; }
  @media (prefers-reduced-motion: reduce) { .miq-section { animation: none; } }
`;
